import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';

/**
 * Updates the recently viewed products in localStorage.
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products in session storage */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {number} The maximum number of products to store */
  static #MAX_PRODUCTS = 10;

  /**
   * Adds a product to the recently viewed products list.
   * @param {string} productId - The ID of the product to add.
   */
  static addProduct(productId) {
    let viewedProducts = this.getProducts();

    viewedProducts = viewedProducts.filter((/** @type {string} */ id) => id !== productId);
    viewedProducts.unshift(productId);
    viewedProducts = viewedProducts.slice(0, this.#MAX_PRODUCTS);

    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));
  }

  static clearProducts() {
    localStorage.removeItem(this.#STORAGE_KEY);
  }

  /**
   * Retrieves the list of recently viewed products from session storage.
   * @returns {string[]} The list of viewed products.
   */
  static getProducts() {
    return JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
  }
}

export class RecentlyViewedProducts extends Component {
  /**
   * The observer for lazy loading the recently viewed products
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadProducts();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
  }

  async #loadProducts() {
    const { sectionId, productId, limit = 4 } = this.dataset;
    console.log('[RecentlyViewed] Initializing loadProducts with dataset:', { sectionId, productId, limit });

    if (!sectionId) {
      console.error('[RecentlyViewed] Error: Section ID is required');
      return;
    }

    // Get viewed products from localStorage
    const viewedIds = RecentlyViewed.getProducts();
    console.log('[RecentlyViewed] Raw IDs from localStorage:', viewedIds);

    // Filter out currently viewed product
    const filteredIds = viewedIds.filter(id => id !== productId).slice(0, parseInt(limit, 10));
    console.log('[RecentlyViewed] Filtered IDs to fetch (excluding current product):', filteredIds);

    if (filteredIds.length === 0) {
      console.log('[RecentlyViewed] No recently viewed products found in history.');
      if (Shopify.designMode) {
        console.log('[RecentlyViewed] Shopify designMode is active; preserving onboarding preview layout.');
        return;
      }
      console.log('[RecentlyViewed] Shopify designMode is inactive; hiding section.');
      this.classList.add('hidden');
      return;
    }

    // Build the query: e.g. id:123 OR id:456
    const query = filteredIds.map(id => `id:${id}`).join(' OR ');
    const routesRoot = window.Shopify ? window.Shopify.routes.root : '/';
    const url = `${routesRoot}search?type=product&q=${encodeURIComponent(query)}&section_id=${sectionId}`;
    console.log('[RecentlyViewed] Fetching products from URL:', url);

    try {
      const response = await fetch(url);
      console.log('[RecentlyViewed] Fetch response status:', response.status, response.statusText);
      if (!response.ok) return;

      const html = await response.text();
      console.log('[RecentlyViewed] Fetch response HTML length:', html.length);
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Locate the section wrapper inside the search results document, wrap it if not present
      let newSectionElement = doc.getElementById(`shopify-section-${sectionId}`);
      console.log('[RecentlyViewed] Finding shopify-section wrapper:', !!newSectionElement);
      
      if (!newSectionElement) {
        console.log('[RecentlyViewed] Wrapper not found in response; dynamically wrapping.');
        const wrapper = doc.createElement('div');
        wrapper.id = `shopify-section-${sectionId}`;
        wrapper.className = 'shopify-section';
        
        if (doc.body) {
          while (doc.body.firstChild) {
            wrapper.appendChild(doc.body.firstChild);
          }
          doc.body.appendChild(wrapper);
        } else {
          doc.appendChild(wrapper);
        }
        newSectionElement = wrapper;
      }

      const hasProducts = newSectionElement.querySelector('[data-has-products="true"]');
      console.log('[RecentlyViewed] Has products ([data-has-products="true"]):', !!hasProducts);
      
      if (!hasProducts) {
        console.log('[RecentlyViewed] No products rendered by search endpoint.');
        if (!Shopify.designMode) {
          console.log('[RecentlyViewed] Shopify designMode is inactive; hiding section.');
          this.classList.add('hidden');
        }
        return;
      }

      // Reorder items to match exact chronological viewed order (most recent first)
      const listContainer = newSectionElement.querySelector('.resource-list--grid') || newSectionElement.querySelector('slideshow-slides');
      console.log('[RecentlyViewed] List container element:', listContainer ? listContainer.className || listContainer.tagName : 'not found');
      
      if (listContainer) {
        const items = Array.from(listContainer.children);
        console.log('[RecentlyViewed] Reordering', items.length, 'rendered items.');
        
        items.sort((a, b) => {
          const idA = a.querySelector('[data-product-id]')?.getAttribute('data-product-id') || a.getAttribute('data-product-id');
          const idB = b.querySelector('[data-product-id]')?.getAttribute('data-product-id') || b.getAttribute('data-product-id');
          return filteredIds.indexOf(idA) - filteredIds.indexOf(idB);
        });

        // Re-append sorted elements
        items.forEach((item, index) => {
          listContainer.appendChild(item);

          // Update slideshow slides references if needed
          if (item.tagName === 'SLIDESHOW-SLIDE') {
            item.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
            item.style.setProperty('--slideshow-timeline', `--slide-${index}`);
            if (item.hasAttribute('on:click')) {
              item.setAttribute('on:click', `/select/${index}`);
            }
          }
        });
      }

      console.log('[RecentlyViewed] Morphing section DOM with morphSection...');
      // Morph the page element using SF Rendering API HTML
      morphSection(sectionId, doc.documentElement.innerHTML, { mode: 'full', injectStylesheet: true });
      console.log('[RecentlyViewed] DOM morph complete!');
    } catch (e) {
      console.error('[RecentlyViewed] Exception occurred during loadProducts:', e);
      if (!Shopify.designMode) {
        console.log('[RecentlyViewed] Shopify designMode is inactive; hiding section on error.');
        this.classList.add('hidden');
      }
    }
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
